import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { PermissionProvider, usePermissionsContext, getProductCode } from './permission/permissionContext';
import { getPermissionMapping } from './permission/permissionMapping';
import { Home } from '@dietary-business-intelligence/vmi-config-package';
import { FilterState } from './types';
import debounce from 'lodash/debounce';
import ErrorBoundary from './components/ErrorBoundary';
import MapComponent from './components/MapComponent';
import Diagnosys from './components/Diagnosys';
import BroadcastWrapper from './components/BroadcastWrapper';
import dayjs from 'dayjs';

const theme = createTheme({
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  palette: {
    primary: {
      main: 'rgb(0, 145, 5)',
    },
    secondary: {
      main: '#1a73e8',
    },
  },
  components: {
    MuiPopover: {
      defaultProps: {
        container: document.body,
        disablePortal: false,
        keepMounted: false,
        slotProps: {
          paper: {
            elevation: 8,
            sx: {
              position: 'fixed',
              zIndex: 99999
            }
          }
        }
      },
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          container: document.body,
          disablePortal: false,
          slotProps: {
            paper: {
              elevation: 8,
              sx: {
                maxHeight: 300,
                position: 'fixed',
                zIndex: 99999,
                mt: 0.5,
                '& .MuiMenuItem-root': {
                  minHeight: 'auto',
                  py: 1
                }
              }
            }
          },
          MenuListProps: {
            sx: {
              maxHeight: 300,
              overflowY: 'auto'
            }
          },
          TransitionProps: {
            mountOnEnter: true,
            unmountOnExit: true
          },
          BackdropProps: {
            invisible: true
          }
        }
      },
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    }
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const APP_PERMISSION_KEYS = ['Map_All', 'primaryMatrics', 'Diagnosis'];

function hasAnyAppAccess(permissions: { success: boolean; access_controls: { [key: string]: { [key: string]: { R: string } } } } | null): boolean {
  if (!permissions?.success) return false;
  return APP_PERMISSION_KEYS.some(key => {
    const mapping = getPermissionMapping(key);
    if (!mapping) return false;
    const controller = permissions.access_controls[mapping.controller];
    return !!(controller && controller[mapping.function]);
  });
}

const AppContent: React.FC = () => {
  const { isLoading } = useAuth();
  const { permissions, loading: permissionsLoading } = usePermissionsContext();
  const location = useLocation();
  const defaultStartOfYear = dayjs().startOf('year').format('YYYY-MM-DD');
  const defaultEndOfYear = dayjs().endOf('year').format('YYYY-MM-DD');
  const [filters, setFiltersImmediate] = useState<FilterState>({
    country: 'United States',
    state: 'California',
    city: '',
    searchTerm: '',
    productSearch: '',
    salesRep: '',
    locationType: ['all', 'client', 'warehouse', 'vendor', 'prospect'],
    location: '',
    mapView: 'standard',
    dateRangePreset: 'this-year',
    startDate: defaultStartOfYear,
    endDate: defaultEndOfYear,
  });

  const [primaryMetricModalVisible, setPrimaryMetricModalVisible] = useState(false);

  const debouncedFilterChange = useCallback((newFilters: FilterState) => {
    debounce((filters: FilterState) => {
      setFiltersImmediate(filters);
    }, 300)(newFilters);
  }, []);

  // Handle Primary Metric modal
  const handlePrimaryMetricClick = () => {
    setPrimaryMetricModalVisible(true);
  };

  const handlePrimaryMetricModalClose = () => {
    setPrimaryMetricModalVisible(false);
  };

  // Determine current view based on route
  const currentView = location.pathname === '/diagnosys' ? 'diagnosys' : 'dashboard';

  if (isLoading || permissionsLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAnyAppAccess(permissions)) {
    const product_code = getProductCode();
    const PRODUCT_PROPS = { product: product_code };
    return <Home {...PRODUCT_PROPS} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f5' }}>
      <Navbar 
        filters={filters}
        onFiltersChange={debouncedFilterChange}
        currentView={currentView}
        onPrimaryMetricClick={handlePrimaryMetricClick}
      />
      <Routes>
        <Route 
          path="/" 
          element={
            <MapComponent 
              filters={filters}
              onFiltersChange={debouncedFilterChange}
              primaryMetricModalVisible={primaryMetricModalVisible}
              onPrimaryMetricModalClose={handlePrimaryMetricModalClose}
            />
          } 
        />
        <Route 
          path="/diagnosys" 
          element={
            <Diagnosys />
          } 
        />
      </Routes>
      <Footer />
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <UserProvider>
            <PermissionProvider>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <BroadcastWrapper>
                  <Router>
                    <AppContent />
                  </Router>
                </BroadcastWrapper>
              </ThemeProvider>
            </PermissionProvider>
          </UserProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
